import * as types from "../mutation-types";
import { roundNumber, getDateString } from "@/util";

const TimePeriodOptions = [
    {
        value: 1,
        labelId: "time_period_trailing_1_year"
    },
    {
        value: 2,
        labelId: "time_period_trailing_3_year"
    },
    {
        value: 3,
        labelId: "time_period_trailing_5_year"
    },
    {
        value: 4,
        labelId: "time_period_trailing_10_year"
    },
    {
        value: 8,
        labelId: "time_period_trailing_15_year"
    },
    {
        value: 5,
        labelId: "time_period_since_inception"
    },
    {
        value: 6,
        labelId: "time_period_earliest_available"
    },
    {
        value: 7,
        labelId: "time_period_earliest_common"
    },
    {
        value: 0,
        labelId: "time_period_custom"
    }
];

const state = {
    securities: [],
    assetAllocations: {}
};

const getters = {
    securitiesWithAllocation(state) {
        return state.securities.map(item => {
            return {
                ...item,
                assetAllocation: state.assetAllocations[item.securityId] || []
            };
        });
    },
    timePeriodOptions(state, getters, rootState, rootGetters) {
        const presetTimePeriod = rootGetters["settings/presetTimePeriod"].map(item => {
            return {
                ...item,
                startDate: getDateString(new Date(item.startDate)),
                endDate: getDateString(new Date(item.endDate))
            };
        });
        if (presetTimePeriod.length > 0) {
            const presetTimePeriodOptions = presetTimePeriod.map(item => {
                const { labelKey, ...presetTimePeriod } = item;
                return {
                    value: `99-${presetTimePeriod.type}`,
                    labelId: labelKey,
                    isPreset: true,
                    presetTimePeriod
                };
            });
            const len = TimePeriodOptions.length;
            return [...TimePeriodOptions.slice(0, len - 1), ...presetTimePeriodOptions, TimePeriodOptions[len - 1]];
        }
        return TimePeriodOptions;
    },
    hasVAOr529Securities(state) {
        // VA: securityType is "VA"; 529: securityType is "5O" || CT"
        const has529 = state.securities.find(security => {
            return ["5O", "CT"].indexOf(security.securityType) !== -1;
        });
        const hasVA = state.securities.find(security => {
            return ["VA"].indexOf(security.securityType) !== -1;
        });
        return { has529, hasVA };
    }
};

const actions = {
    getSecurityLevelAssetAllocation({ state, commit, rootGetters }, { fetch }) {
        const assetGroups = rootGetters["settings/broadAssetAllocationGroup"];
        const securitiesIdArr = state.securities.filter(i => !state.assetAllocations[i.securityId]).map(i => i.securityId);
        if (securitiesIdArr.length === 0) {
            return false; // no need re-get asset allocation if data exist.
        }
        return fetch({
            url: `/api/v1/security/assetallocation`,
            method: "POST",
            body: JSON.stringify({
                securityIds: securitiesIdArr,
                assetGroups
            })
        })
            .then(result => {
                const assetAllocationGetter = result => {
                    const { data, groupDefine } = result;
                    const groupId = groupDefine.groupId;
                    //AWS admin tool => 0:Bar Chart 1:Pie Chart 2:Simple Pie or Bar Chart
                    const chartType = groupDefine.chartType;
                    const assetClassMap = Object.keys(groupDefine.compItems).reduce((prev, assetClassId) => {
                        const { order, color, className } = groupDefine.compItems[assetClassId];
                        prev.push({
                            assetClassId: assetClassId,
                            name: className,
                            color: color,
                            order: order
                        });
                        return prev;
                    }, []);
                    return secId => {
                        const getAssetClassPropValue = (assetClassId, propKey) => {
                            const currentAssetClass = assetClassMap.find(item => item.assetClassId === assetClassId);
                            return currentAssetClass && currentAssetClass[propKey];
                        };
                        if (data[secId] && data[secId][groupId] && data[secId][groupId].length > 0) {
                            return data[secId][groupId]
                                .sort(function(a, b) {
                                    return getAssetClassPropValue(a.assetClassId, "order") - getAssetClassPropValue(b.assetClassId, "order");
                                })
                                .map(i => {
                                    const isUsedRescaledLong = chartType === 1;
                                    const weight = isUsedRescaledLong && typeof i.rlWeight !== "undefined" ? i.rlWeight : i.netWeight;
                                    return {
                                        weight: roundNumber(weight).toFixed(2),
                                        color: getAssetClassPropValue(i.assetClassId, "color"),
                                        name: getAssetClassPropValue(i.assetClassId, "name")
                                    };
                                });
                        }
                        return [];
                    };
                };
                const assetAllocations = {
                    ...state.assetAllocations
                };
                const getAssetAllocationBySecId = assetAllocationGetter(result);
                securitiesIdArr.forEach(id => {
                    assetAllocations[id] = getAssetAllocationBySecId(id);
                });
                commit(types.SET_ASSET_ALLOCATIONS, assetAllocations);
            })
            .catch(error => {
                console.error(error);
            });
    }
};

const mutations = {
    [types.RESET](state) {
        state.securities = [];
        state.assetAllocations = {};
    },
    [types.SET_SECURITIES](state, data) {
        state.securities = data;
    },
    [types.SET_ASSET_ALLOCATIONS](state, data) {
        state.assetAllocations = data;
    }
};

export default {
    state,
    getters,
    mutations,
    actions
};
